import React, { type ReactElement, type ReactNode } from "react";
import { Plus } from "lucide-react";
import { Skeleton, cn } from "@ndd/ui";
import { formatRelativeShort } from "../../../../shared/local-date";
import type {
  WorkspaceOtherOwnerThreads,
  WorkspaceThreadSummary,
} from "../../../../shared/api-types";
import { rdFirstName } from "../../lib/rd-chats";
import { chatThreadLabel } from "../../lib/rd-page-tabs";
import {
  THEMED_BAND,
  THEMED_CARD,
  THEMED_CARD_TITLE,
  THEMED_DIVIDE,
  THEMED_EYEBROW,
  THEMED_RULE,
} from "../../lib/always-look";

// The RD page's "Chats about <RD>" card in the single-rail frame (NO-754): the
// viewer's own chats about the RD and, for a viewer who may read everyone's
// (`chats:read:all`), other owners' chats, each marked read-only. A leaf
// module (`import React`, relative imports, no router, no queries) so
// `tsx --test` renders it; the composer (rd-chats-card-slot.tsx) supplies the
// summary and real links through `renderLink`.

export type RdChatsCardTarget =
  | { to: "/rds/$rdId/chats/new" }
  | { to: "/rds/$rdId/chats/$chatId"; chatId: string };

export interface RdChatsCardLinkProps {
  target: RdChatsCardTarget;
  className: string;
  children: ReactNode;
}

export type RenderRdChatsCardLink = (props: RdChatsCardLinkProps) => ReactElement;

export type RdChatsCardState =
  | { status: "loading" }
  | { status: "error" }
  | {
      status: "ready";
      threads: WorkspaceThreadSummary[];
      otherOwners: WorkspaceOtherOwnerThreads[];
    };

// Visual grammar copied from snapshot-profile-card.tsx, where these constants
// are module-private. Under the always theme (NO-792) it is a white card with
// a card-white band, a display-type title and hairline rules.
const CARD = cn(
  "overflow-hidden rounded-[14px] border border-border bg-card shadow-[0_1px_2px_rgba(40,3,15,0.05)]",
  THEMED_CARD
);
const CARD_HEAD = cn(
  "flex min-h-[48px] items-center justify-between gap-3 border-b border-border bg-muted px-4 py-2",
  THEMED_BAND
);
const CARD_TITLE = cn(
  "font-sans text-label font-extrabold uppercase tracking-[0.06em] text-foreground",
  THEMED_CARD_TITLE
);
const ROW =
  "flex items-center justify-between gap-3 px-4 py-2.5 transition-colors hover:bg-sand-50";
const ROW_LIST = cn("divide-y divide-border", THEMED_DIVIDE);
const QUIET = "px-4 py-4 text-body text-muted-strong";

interface OtherOwnerChat {
  ownerName: string;
  thread: WorkspaceThreadSummary;
}

function othersOf(otherOwners: WorkspaceOtherOwnerThreads[]): OtherOwnerChat[] {
  return otherOwners.flatMap((owner) =>
    owner.threads.map((thread) => ({ ownerName: owner.ownerName, thread }))
  );
}

function ChatRow({
  thread,
  ownerName,
  renderLink,
}: {
  thread: WorkspaceThreadSummary;
  ownerName?: string;
  renderLink: RenderRdChatsCardLink;
}): ReactElement {
  return (
    <li>
      {renderLink({
        target: { to: "/rds/$rdId/chats/$chatId", chatId: thread.chatId },
        className: ROW,
        children: (
          <>
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-body-lg font-medium text-foreground">
                {chatThreadLabel(thread)}
              </span>
              {ownerName === undefined ? null : (
                <span className="truncate text-xs text-muted-foreground">
                  {ownerName} · read-only
                </span>
              )}
            </span>
            <span className="shrink-0 text-xs font-medium text-subtle">
              {formatRelativeShort(thread.updatedAt)}
            </span>
          </>
        ),
      })}
    </li>
  );
}

function OwnChats({
  firstName,
  threads,
  renderLink,
}: {
  firstName: string;
  threads: WorkspaceThreadSummary[];
  renderLink: RenderRdChatsCardLink;
}): ReactElement {
  if (threads.length === 0) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4">
        <p className="text-body text-muted-strong">No chats about {firstName} yet.</p>
        {renderLink({
          target: { to: "/rds/$rdId/chats/new" },
          className:
            "inline-flex items-center gap-1.5 font-sans text-body font-semibold text-primary hover:underline",
          children: (
            <>
              <Plus aria-hidden="true" className="size-[14px]" />
              New chat
            </>
          ),
        })}
      </div>
    );
  }

  return (
    <ul className={ROW_LIST}>
      {threads.map((thread) => (
        <ChatRow key={thread.chatId} thread={thread} renderLink={renderLink} />
      ))}
    </ul>
  );
}

function OtherOwnersChats({
  otherOwners,
  renderLink,
}: {
  otherOwners: WorkspaceOtherOwnerThreads[];
  renderLink: RenderRdChatsCardLink;
}): ReactElement | null {
  const others = othersOf(otherOwners);
  if (others.length === 0) return null;

  return (
    <div className={cn("border-t border-border", THEMED_RULE)}>
      <p
        className={cn(
          "px-4 pb-1 pt-3 font-sans text-caption font-bold uppercase tracking-[0.08em] text-subtle",
          THEMED_EYEBROW
        )}
      >
        Other people&rsquo;s chats
      </p>
      <ul className={ROW_LIST}>
        {others.map(({ ownerName, thread }) => (
          <ChatRow
            key={thread.chatId}
            thread={thread}
            ownerName={ownerName}
            renderLink={renderLink}
          />
        ))}
      </ul>
    </div>
  );
}

function CardBody({
  firstName,
  state,
  renderLink,
}: {
  firstName: string;
  state: RdChatsCardState;
  renderLink: RenderRdChatsCardLink;
}): ReactElement {
  if (state.status === "loading") {
    return (
      <div aria-hidden="true" className="flex flex-col gap-3 px-4 py-4">
        <Skeleton className="h-4 w-56" />
        <Skeleton className="h-4 w-40" />
      </div>
    );
  }

  if (state.status === "error") {
    return <p className={QUIET}>Chats about {firstName} aren&rsquo;t available.</p>;
  }

  return (
    <>
      <OwnChats firstName={firstName} threads={state.threads} renderLink={renderLink} />
      <OtherOwnersChats otherOwners={state.otherOwners} renderLink={renderLink} />
    </>
  );
}

export function RdChatsCard({
  rdName,
  state,
  renderLink,
}: {
  rdName: string;
  state: RdChatsCardState;
  renderLink: RenderRdChatsCardLink;
}): ReactElement {
  const firstName = rdFirstName(rdName);
  const title = `Chats about ${firstName}`;

  return (
    <section className={CARD} aria-label={title}>
      <div className={CARD_HEAD}>
        <h2 className={CARD_TITLE}>{title}</h2>
      </div>
      <CardBody firstName={firstName} state={state} renderLink={renderLink} />
    </section>
  );
}
